import { PersonSearchSelect } from '~/pages/person/components/person-search-select';
import { useUnsavedChangesGuard } from '~/hooks/useUnsavedChangesGuard';
import { JudgeWrite, JudgeWriteRequest } from '~/apis/ssf';
import { isValidationError } from '~/utils/validation';
import { schema, Schema } from './referee-form.schema';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '~/components/Button/Button';
import { Grid } from '~/components/Grid/Grid';
import { Input } from '~/components/Input';
import { useForm } from 'react-hook-form';
import { AxiosResponse } from 'axios';
import _ from 'lodash';

interface Props {
  onSubmit: (data: Schema) => Promise<AxiosResponse<JudgeWrite, Error>>;
  onSuccess: (response: JudgeWrite) => void;
  mode: 'create' | 'edit';
  defaultValues?: Schema;
}

export function RefereeForm({ defaultValues, mode, onSubmit, onSuccess }: Props) {
  const { formState, register, handleSubmit, setValue, trigger, setError, watch } = useForm({
    defaultValues: defaultValues ?? {},
    resolver: zodResolver(schema),
  });

  const { dialog } = useUnsavedChangesGuard(formState);

  async function trySubmit(payload: Schema) {
    try {
      const { data } = await onSubmit(payload);
      onSuccess(data);
    } catch (error) {
      if (isValidationError<JudgeWriteRequest>(error)) {
        for (const [field, errors] of _.entries(error.response?.data ?? {})) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-explicit-any
          setError(field as any, { message: errors[0] });
        }
      }
    }
  }

  return (
    <form onSubmit={handleSubmit(trySubmit)} autoComplete="off">
      <Grid>
        <Grid.Item cols={{ sm: 12, md: 6 }}>
          <Input.Group formState={formState} htmlFor="person" label="Person">
            <PersonSearchSelect
              {...register('person')}
              onChange={person => {
                setValue('person', person as [string, number], { shouldDirty: true });
                trigger('person');
              }}
              placeholder="Sök efter person..."
              defaultValue={watch('person')}
              formState={formState}
              name="person"
            />
          </Input.Group>
        </Grid.Item>

        <Grid.Item cols={{ sm: 12, md: 6 }}>
          <Input.Group formState={formState} htmlFor="book" label="Boknummer">
            <Input.Field type="number" {...register('book')} formState={formState} />
          </Input.Group>
        </Grid.Item>
      </Grid>

      <Grid>
        <Grid.Item cols={{ sm: 12, md: 6 }}>
          <Input.Group formState={formState} htmlFor="status" label="Status">
            <Input.Select
              {...register('status')}
              placeholder="Välj status"
              formState={formState}
              options={[
                ['Aktiv', 'active'],
                ['Varnad', 'warned'],
                ['Avstängd', 'suspended'],
                ['Okänd', 'unknown'],
              ]}
            />
          </Input.Group>
        </Grid.Item>
      </Grid>

      <Button.Group align="right">
        <Button type="submit">{{ create: 'Skapa', edit: 'Spara' }[mode]}</Button>
      </Button.Group>

      {dialog}
    </form>
  );
}
